import { computed } from 'vue';
import { useAuthStore } from '../../../../store/modules/auth/composables';
import { useUsrSettingStore } from '../../../../store/modules/user_setting/composables';

export function useLoginPageLogic() {
    /*
     * composables
     */
    const { errorCode, errorMsg, embedded, error, dealError: authDealError, dealNotice, notice } = useAuthStore();
    const {
        beforeLoginDetails,
        dealError: beforeLoginDealError,
        userSettingDetails: userSetting,
    } = useUsrSettingStore();

    /*
     * setter, getter
     */
    const beforeLoginSuccess = computed(
        () => !beforeLoginDetails.value.isProcessing && !beforeLoginDetails.value.error
    );
    const receiveTheUnlockMail = { id: 1, title: 'Resend_Unlocked_Mail', name: "I_didn't_receive_the_Unlock_Email" };
    const forgotThePassword = { id: 2, title: 'Send_Password_Reset_Mail', name: 'I_forgot_the_password' };
    const receiveMyConfirmationMail = {
        id: 3,
        title: 'Resend_Identity_Verification_Mail',
        name: "I_didn't_receive_an_Identity_Confirmation_Email",
    };

    const authErrorMessage = computed(() => `${errorCode.value} - ${errorMsg.value}`);
    const embeddedErrorMessage = computed(() => `${embedded.value.errorCode} - ${embedded.value.errorMsg}`);
    const beforeLoginErrorMessage = computed(
        () => `${beforeLoginDetails.value.errorCode} - ${beforeLoginDetails.value.errorMsg}`
    );
    const authError = computed(() => error.value);
    const embeddedError = computed(() => embedded.value.error);
    const beforeLoginError = computed(() => beforeLoginDetails.value.error);
    const anyError = computed({
        get() {
            return authError.value || embeddedError.value || beforeLoginError.value;
        },
        set() {
            if (authError.value || embeddedError.value) {
                authDealError();
            }
            if (beforeLoginError.value) {
                beforeLoginDealError();
            }
        },
    });

    const anyErrorMessage = computed(() => {
        if (authError.value) return authErrorMessage.value;
        if (embeddedError.value) return embeddedErrorMessage.value;
        if (beforeLoginError.value) return beforeLoginErrorMessage.value;
        return '';
    });
    const anyNotice = computed({
        get() {
            return !!anyNoticeMessage.value;
        },
        set(val) {
            if (embedded.value.notice || notice.value) {
                dealNotice();
            }
        },
    });
    const anyNoticeMessage = computed(() => embedded.value.notice || notice.value);
    const domainName = computed(() => {
        if (!userSetting.value?.domain?.enabled) return '';
        if (!userSetting.value?.domain?.domain_name) return '';
        return userSetting.value.domain.domain_name;
    });
    const samlEnabled = computed(() => {
        if (!userSetting.value?.saml) return false;

        return userSetting.value?.saml.enabled;
    });
    const isFetchedMultiAuthSettingData = computed(() => {
        return !!userSetting.value?.multifactor;
    });
    const isMultiAuth = computed(() => {
        return isFetchedMultiAuthSettingData.value ? userSetting.value.multifactor.status === 'enable' : false;
    });
    const isHardwareTokenRegister = computed(() => {
        if (!isFetchedMultiAuthSettingData.value) {
            return false;
        }
        return !(userSetting.value.multifactor.status === 'no') && userSetting.value.multifactor.device_type === 'hard';
    });
    const isSoftDeviceRegisterable = computed(() => {
        if (!isFetchedMultiAuthSettingData.value) return false;
        return !(userSetting.value.multifactor.status === 'no') && userSetting.value.multifactor.device_type === 'soft';
    });
    const isAccountLock = computed(() => {
        if (!userSetting.value?.account_lock) return false;
        return userSetting.value.account_lock.enabled && userSetting.value.account_lock.send_unlock_mail;
    });
    const isVerificationMail = computed(() => {
        if (!userSetting.value?.verification_mail) return false;
        return userSetting.value.verification_mail.enabled;
    });
    const mailDialogMenu = computed(() => {
        const menus = [];

        if (isAccountLock.value) {
            menus.push(receiveTheUnlockMail);
        }
        menus.push(forgotThePassword);
        if (isVerificationMail.value) {
            menus.push(receiveMyConfirmationMail);
        }
        return menus;
    });

    return {
        anyNotice,
        anyNoticeMessage,
        anyError,
        anyErrorMessage,
        beforeLoginSuccess,
        domainName,
        samlEnabled,
        isAccountLock,
        isVerificationMail,
        isMultiAuth,
        isHardwareTokenRegister,
        isSoftDeviceRegisterable,
        mailDialogMenu,
    };
}
